/**
 * Shared mechanics for the carrier widgets' external assets.
 *
 * Each carrier keeps its own policy — when an asset is reusable, what signals
 * readiness, whether a fresh copy is needed per open. This module owns the one
 * shape they all repeat: an element that answers `load` or `error` exactly
 * once, and the creation of the element itself.
 */

/** Resolves on the element's `load`, rejects on its `error`. */
export const loadOf = (element: Element, description: string): Promise<void> =>
  new Promise((resolve, reject) => {
    element.addEventListener('load', () => {
      resolve();
    });
    element.addEventListener('error', () => {
      reject(new Error(`Failed to load ${description}`));
    });
  });

export const createScript = (
  url: string,
  attributes: Record<string, string> = {}
): HTMLScriptElement => {
  const script = document.createElement('script');
  for (const [name, value] of Object.entries(attributes)) {
    script.setAttribute(name, value);
  }
  script.src = url;
  script.async = true;
  return script;
};

export const createStylesheet = (
  url: string,
  attributes: Record<string, string> = {}
): HTMLLinkElement => {
  const link = document.createElement('link');
  for (const [name, value] of Object.entries(attributes)) {
    link.setAttribute(name, value);
  }
  link.rel = 'stylesheet';
  link.href = url;
  return link;
};
